import uniqBy from 'lodash.uniqby';

import { getTrailsDissolvedLayer, fetchTrailsBySearchTerm } from 'data/map';
import Services from 'configs/Services.json';

import { DetourArea, DetourCity, DetourPlace, DetourTrail, DetourManager } from 'icons/icons';

import SearchVM from '@arcgis/core/widgets/Search/SearchViewModel';
import Extent from '@arcgis/core/geometry/Extent';

const icons = (option) => {
  switch (option) {
    case '(0)':
      return <DetourPlace />;
    case '(1)':
      return <DetourTrail />;
    case '(2)':
      return <DetourCity />;
    case '(3)':
      return <DetourArea />;
    case '(4)':
      return <DetourManager />;
    default:
      return <></>;
  }
};

const init = async ({ view }) => {
  const vm = new SearchVM({
    view,
    // TODO: I turned off "By Address" in the webmap settings, but World Geocoder is still in default sources...
    includeDefaultSources: false,
    autoSelect: false,
    sources: [
      {
        layer: getTrailsDissolvedLayer(),
        searchFields: ['MANAGED_AREA_NAME'],
        displayField: 'MANAGED_AREA_NAME',
        suggestionTemplate: '{MANAGED_AREA_NAME}',
        exactMatch: false,
        outFields: ['*'],
        name: 'Managed Area Search',
        placeholder: 'Search here',
        maxResults: 10,
        maxSuggestions: 10,
        suggestionsEnabled: true,
        minSuggestCharacters: 2,
      },
      {
        layer: getTrailsDissolvedLayer(),
        searchFields: ['PRIMARY_NAME', 'PRIMARY_TYPE'],
        displayField: 'PRIMARY_NAME',
        suggestionTemplate: '{PRIMARY_NAME};;{MANAGED_AREA_NAME};;{PRIMARY_TRAIL_ID}',
        exactMatch: false,
        outFields: ['*'],
        name: 'Primary Search',
        placeholder: 'Search here',
        maxResults: 10,
        maxSuggestions: 10,
        suggestionsEnabled: true,
        minSuggestCharacters: 2,
      },
      {
        locator: {
          url: Services.CUSTOM_LOCATOR,
        },
        singleLineFieldName: 'SingleLine',
        name: 'Ohio Search',
        placeholder: 'Search Ohio',
        maxResults: 1000,
        maxSuggestions: 30,
        minSuggestCharacters: 2,
        countryCode: 'US',
        filter: {
          geometry: new Extent({
            xmin: -85.23193359375,
            ymin: 38.232421875,
            xmax: -80.31005859375,
            ymax: 42.34130859375,
          }),
        },
      },
    ],
    goToOverride: () => {
      // Don't call view.goTo()
      return null;
    },
  });

  return vm;
};

const suggest = async ({ vm, value }) => {
  // Use the SearchViewModel to get suggestions
  const suggestResponse = await vm.suggest(value);
  let formattedResults = [];
  // Create a lookup for our sources
  const sourceNameLookup = {};
  vm.allSources.forEach((source, i) => {
    sourceNameLookup[i] = abbrevSourceName[source.name];
  });

  // Flatten managed area results into a single array
  suggestResponse.results[0].results = uniqBy(suggestResponse.results[0].results, (e) => e.text);
  let allResults = suggestResponse.results[2].results;

  let trailInfo = [];
  // suggestResponse.results.forEach(({ results }) => {
  //   allResults = [...allResults, ...results];
  // });
  //Format results before returning to TrailSearch component

  formattedResults = allResults.map((suggestResult) => {
    trailInfo = suggestResult.text.split(';;');
    const text = trailInfo[0];
    // Add icons
    const options = ['(0)', '(1)', '(2)', '(3)', '(4)'];
    const optionValue = options.find((op) => {
      return text.includes(op);
    });
    const optionIndex = text.indexOf(optionValue);
    const optionText = text.substring(0, optionIndex);
    const displayText = optionText.charAt(0).toUpperCase() + optionText.slice(1);

    const displayIcon = icons(optionValue);

    return {
      displayText: displayText,
      displayIcon: displayIcon,
      optionValue: optionValue,
      text: displayText, //text
      managedAreaName: trailInfo[1],
      trailId: trailInfo[2],
      key: `${suggestResult.key}_${suggestResult.sourceIndex}`,
      source: sourceNameLookup[suggestResult.sourceIndex],
      suggestResult,
    };
  });

  return formattedResults;
};

const search = async ({ vm, value, ...props }) => {
  // console.log("search", value, props);
  const { optionValue, suggestResult, mapView } = props || {};

  let response = {
    searchResponse: null,
    searchExtentLocation: null,
    searchCards: null,
    searchTrails: null,
  };

  const searchResponse = await vm.search(suggestResult.text);
  response.searchResponse = searchResponse;

  /** (2),(3),(4) (City,ManagedArea,Manager)
   * run FindAddress on the user-selected suggest string
   * strip out the place-name (the suggest string minus the (#) suffix)
   * save the individual trails (1) results that are returned for this 1st FindAddress call (which is all trails linked for that area and already sorted by Length)
   * add suffix ' (0)' to the place-name then run FindAddress on that string from that Place result get the Extent
   * zoom map to that extent
   * create and display Trail cards for the saved individual trails (1) results from the 1st FindAddress query.  This will limit the Trail cards to only those belonging to the ManagedArea,City, or Manager that the user clicked from Suggest outputs.
   * */
  const options = [['(2)', '(3)', '(4)'], ['(1)'], ['(5)'], ['(0)']];

  if (options[0].includes(optionValue)) {
    // results[2] = locator address search
    // keep (1) for trail cards

    response.searchCards = searchResponse.results[2].results.filter((res) => res.name.includes('(1)'));

    // UNIQUE_NAME
    const searchTermList = response.searchCards.map((card) => {
      //TODO : Get PRIMARY_TRAIL_ID
      const name = card.name.split(' (')[0];

      return `UNIQUE_NAME='${name.replaceAll("'", "''")}'`;
    });

    let searchTerm = searchTermList.join(' OR ');

    if (searchTerm?.length > 11) {
      try {
        const trails = await fetchTrailsBySearchTerm(mapView, searchTerm);
        response.searchTrails = trails;
      } catch (err) {
        console.warn('Error searching for trails.', err);
        response.searchTrails = [];
      }
    } else {
      response.searchTrails = [];
    }

    // replace '(#)' with '(0)' and run FindAddress
    //  zoom to that extent
    const location = value + '(0)';
    const searchAddress = await vm.search(location);
    const addressResults = searchAddress.results[2].results.filter((record) => {
      return record.name.includes('(0)');
    });
    response.searchExtentLocation = addressResults[0].extent;

    if (searchResponse.results[2].results?.length < 1) {
      searchResponse.results[2].results = addressResults;
    }
  }

  /** (1)
   * run FindAddress on the user-selected suggest string
   * zoom to that trail's extent (from the XMIN...YMAX result values) and draw it in selected (hilited) color
   * build the single Trail card for it- put that at top of cards list, {could add cards for other trails that are visible in that extent too}.
   */
  if (options[1].includes(optionValue)) {
    response.searchCards = searchResponse.results[2].results;
    response.searchExtentLocation = searchResponse.results[2].results[0].extent;
  }

  /** (5)
   * run FindAddress on the user-selected suggest string
   * this is probably going to be a lot of individual Trail (1) results – if possible build a list that looks like the Suggest results list but with these individual trails ‘<name> (1)’ lines
   * then from what the user clicks from that list, use the processing for ‘suffix (1) an individual trail’ listed above.
   */
  if (options[2].includes(optionValue)) {
    response.searchCards = searchResponse.results[2].results.filter((res) => res.name.includes('(1)'));
    response.searchExtentLocation = searchResponse.results[2].results[0].extent;
  }

  /** (0)
   * run FindAddress on the user-selected suggest string
   * zoom to that place's extent (from the XMIN...YMAX result values)
   * then do the current apps' steps to query for all trails visible and build trail cards for them.  HERE IF POSSIBLE - use 'order by Length_SPC DESC' in the query for trails, so that they are ordered longest : shortest in that results and in the trail cards.
   */
  if (options[3].includes(optionValue)) {
    response.searchCards = searchResponse.results[2].results;
    response.searchExtentLocation = searchResponse.results[2].results[0].extent;
  }
  return response;
};

const abbrevSourceName = {
  'Trails: name': 'Trail',
  'Trailheads: name': 'Trailhead',
  'ArcGIS World Geocoding Service': 'Place',
  'Ohio Search': 'Place',
};

export { init, suggest, search };
